import { useState, useEffect, useRef } from "react";
import { api, apiError, retryRequest } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";
import { Combobox } from "@/components/wizard/Combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Building2, FolderKanban, ImagePlus, Save, DownloadCloud, X } from "lucide-react";

const Field = ({ label, id, value, onChange, placeholder, type = "text" }) => (
  <div className="space-y-1.5">
    <Label className="text-muted-foreground text-sm">{label}</Label>
    <Input data-testid={id} value={value || ""} type={type} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="border-border focus-visible:ring-primary/50 focus-visible:ring-2 h-10" />
  </div>
);

export default function Step1Project({ project, catalog, reload, onNext }) {
  const [f, setF] = useState({
    name: project.name || "", client_name: project.client_name || "",
    client_email: project.client_email || "",
    project_number: project.project_number || "", country: project.country || "",
    commodities: project.commodities || (project.commodity ? [project.commodity] : []),
    consultant_name: project.consultant_name || "", consultant_address: project.consultant_address || "",
    consultant_city: project.consultant_city || "", consultant_state: project.consultant_state || "",
    consultant_country: project.consultant_country || "", consultant_phone: project.consultant_phone || "",
    consultant_email: project.consultant_email || "", consultant_website: project.consultant_website || "",
  });
  const [logoPreview, setLogoPreview] = useState(project.branding?.logo || "");
  const [clientLogoPreview, setClientLogoPreview] = useState(project.branding?.client_logo || "");
  const [justUploaded, setJustUploaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoState, setAutoState] = useState("");
  const autoTimer = useRef(null);
  const firstRun = useRef(true);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  // Autosave Step 1 fields (debounced) so a refresh never discards entered details.
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (!f.name.trim()) return;
    if (autoTimer.current) clearTimeout(autoTimer.current);
    autoTimer.current = setTimeout(async () => {
      setAutoState("saving");
      try {
        await retryRequest(() => api.put(`/projects/${project.id}`, f), { retries: 1, delay: 2000 });
        setAutoState("saved");
      } catch { setAutoState(""); }
    }, 1200);
    return () => { if (autoTimer.current) clearTimeout(autoTimer.current); };
  }, [f]);

  const commodityOptions = catalog?.commodity_profiles
    ? Object.entries(catalog.commodity_profiles).map(([k, v]) => ({ value: k, label: v.label || k }))
    : (catalog?.commodities || []).map((c) => ({ value: c, label: c }));

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g)$/i.test(file.name)) return toast.error("Please upload a PNG or JPG logo");
    const reader = new FileReader();
    reader.onload = () => { setLogoPreview(reader.result); setJustUploaded(true); };
    reader.readAsDataURL(file);
    const fd = new FormData();
    fd.append("company", f.consultant_name || "");
    fd.append("logo", file);
    try {
      await api.post(`/projects/${project.id}/branding`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("✓ Logo uploaded successfully");
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadClientLogo = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g)$/i.test(file.name)) return toast.error("Please upload a PNG or JPG logo");
    const reader = new FileReader();
    reader.onload = () => setClientLogoPreview(reader.result);
    reader.readAsDataURL(file);
    const fd = new FormData();
    fd.append("company", f.consultant_name || "");
    fd.append("client_logo", file);
    try {
      await api.post(`/projects/${project.id}/branding`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("✓ Licence holder logo uploaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadDefaults = async () => {
    try {
      const { data } = await api.get("/me/branding");
      if (!data || !Object.keys(data).length) return toast.info("No saved company defaults yet");
      setF((p) => ({ ...p,
        consultant_name: data.company || p.consultant_name, consultant_address: data.address || p.consultant_address,
        consultant_city: data.city || p.consultant_city, consultant_state: data.state || p.consultant_state,
        consultant_country: data.country || p.consultant_country, consultant_phone: data.phone || p.consultant_phone,
        consultant_email: data.email || p.consultant_email, consultant_website: data.website || p.consultant_website }));
      if (data.logo) {
        setLogoPreview(data.logo);
        await api.put(`/projects/${project.id}/branding-json`, { company: data.company, color: data.color, logo: data.logo });
      }
      toast.success("Loaded company defaults");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveDefaults = async () => {
    try {
      await api.put("/me/branding", {
        company: f.consultant_name, address: f.consultant_address, city: f.consultant_city,
        state: f.consultant_state, country: f.consultant_country, phone: f.consultant_phone,
        email: f.consultant_email, website: f.consultant_website,
        logo: logoPreview && logoPreview.startsWith("data:") ? logoPreview : (project.branding?.logo || undefined),
      });
      toast.success("Saved as your company default - new projects will reuse this");
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Project name is required");
    setSaving(true);
    try {
      // Auto-retry once after 2s so a brief dev hot-reload (502/503/504/network) doesn't block the wizard
      await retryRequest(() => api.put(`/projects/${project.id}`, f), { retries: 1, delay: 2000 });
      await reload();
      toast.success("Project details saved");
      onNext();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="step1-project">
      <div className="bg-card border border-border rounded-xl shadow-sm p-8">
        <div className="flex items-center gap-2 mb-6">
          <FolderKanban className="text-primary" size={20} />
          <h2 className="text-lg font-bold text-foreground tracking-tight">Project Details</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Project Name *" id="p1-name" value={f.name} onChange={set("name")} placeholder="e.g. Lupa Goldfield Licence" />
          <Field label="Client Name" id="p1-client" value={f.client_name} onChange={set("client_name")} placeholder="Licence holder" />
          <Field label="Client Email" id="p1-client-email" type="email" value={f.client_email} onChange={set("client_email")} placeholder="client@company.com - lets you email the brief straight to them" />
          <Field label="Project Number" id="p1-number" value={f.project_number} onChange={set("project_number")} placeholder="Auto-generated, e.g. REM2026-001" />
          <div className="space-y-1.5">
            <Label className="text-muted-foreground text-sm">Country</Label>
            <Combobox testid="p1-country" options={COUNTRIES} value={f.country} onChange={set("country")} placeholder="Search country…" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-muted-foreground text-sm">Target Minerals <span className="text-muted-foreground/70 font-normal">(select one or more)</span></Label>
            {(f.commodities || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="p1-commodity-chips">
                {(f.commodities || []).map((c) => (
                  <span key={c} className="inline-flex items-center gap-1 text-xs font-semibold bg-primary/10 text-primary border border-primary/30 rounded-full pl-2 pr-1 py-0.5">
                    {commodityOptions.find((o) => o.value === c)?.label || c}
                    <button type="button" data-testid={`p1-commodity-rm-${c}`} onClick={() => set("commodities")((f.commodities || []).filter((x) => x !== c))} className="hover:text-red-600"><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
            <Combobox testid="p1-commodity" options={commodityOptions.filter((o) => !(f.commodities || []).includes(o.value))} value="" onChange={(v) => { if (v && !(f.commodities || []).includes(v)) set("commodities")([...(f.commodities || []), v]); }} placeholder="Search & add commodity…" />
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl shadow-sm p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Building2 className="text-primary" size={20} />
            <h2 className="text-lg font-bold text-foreground tracking-tight">Consulting Company Details</h2>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" className="border-border text-foreground" onClick={loadDefaults} data-testid="load-defaults-btn">
              <DownloadCloud size={14} className="mr-1.5" /> Load Company Defaults
            </Button>
            <Button type="button" variant="outline" size="sm" className="border-border text-foreground" onClick={saveDefaults} data-testid="save-defaults-btn">
              <Save size={14} className="mr-1.5" /> Save as Default
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Company Name" id="p1-c-name" value={f.consultant_name} onChange={set("consultant_name")} placeholder="Your consulting company" />
          <Field label="Address" id="p1-c-address" value={f.consultant_address} onChange={set("consultant_address")} />
          <Field label="City" id="p1-c-city" value={f.consultant_city} onChange={set("consultant_city")} />
          <Field label="State / Province" id="p1-c-state" value={f.consultant_state} onChange={set("consultant_state")} />
          <div className="space-y-1.5">
            <Label className="text-muted-foreground text-sm">Country</Label>
            <Combobox testid="p1-c-country" options={COUNTRIES} value={f.consultant_country} onChange={set("consultant_country")} placeholder="Search country…" />
          </div>
          <Field label="Phone" id="p1-c-phone" value={f.consultant_phone} onChange={set("consultant_phone")} />
          <Field label="Email" id="p1-c-email" type="email" value={f.consultant_email} onChange={set("consultant_email")} />
          <Field label="Website" id="p1-c-website" value={f.consultant_website} onChange={set("consultant_website")} placeholder="https://" />
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <Label className="text-muted-foreground text-sm">Company Logo</Label>
          <div className="text-[11px] text-muted-foreground mt-0.5">Used in report and map branding</div>
          <div className="mt-2 flex items-center gap-5">
            <div className="h-20 w-32 rounded-lg border border-dashed border-border bg-muted flex items-center justify-center overflow-hidden">
              {logoPreview
                ? <img src={logoPreview} alt="logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
                : <ImagePlus className="text-muted-foreground" size={26} />}
            </div>
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer">
                <input type="file" accept=".png,.jpg,.jpeg" className="hidden" data-testid="logo-input"
                  onChange={(e) => uploadLogo(e.target.files?.[0])} />
                <span className="inline-flex items-center gap-2 px-4 h-10 rounded-md border border-border text-sm font-medium text-foreground hover:border-primary/60 hover:text-primary">
                  <ImagePlus size={15} /> Upload PNG / JPG
                </span>
              </label>
              {justUploaded
                ? <span className="text-[12px] text-emerald-600 font-medium" data-testid="logo-uploaded-ok">✓ Logo uploaded successfully</span>
                : logoPreview && <span className="text-[12px] text-muted-foreground" data-testid="logo-current">Current logo</span>}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <Label className="text-muted-foreground text-sm">Licence Holder Logo (optional)</Label>
          <div className="text-[11px] text-muted-foreground mt-0.5">Shown on the report cover &amp; header alongside your company mark</div>
          <div className="mt-2 flex items-center gap-5">
            <div className="h-20 w-32 rounded-lg border border-dashed border-border bg-muted flex items-center justify-center overflow-hidden">
              {clientLogoPreview
                ? <img src={clientLogoPreview} alt="client logo" className="max-h-full max-w-full object-contain" data-testid="client-logo-preview" />
                : <ImagePlus className="text-muted-foreground" size={26} />}
            </div>
            <label className="cursor-pointer">
              <input type="file" accept=".png,.jpg,.jpeg" className="hidden" data-testid="client-logo-input"
                onChange={(e) => uploadClientLogo(e.target.files?.[0])} />
              <span className="inline-flex items-center gap-2 px-4 h-10 rounded-md border border-border text-sm font-medium text-foreground hover:border-primary/60 hover:text-primary">
                <ImagePlus size={15} /> Upload Holder Logo
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center gap-4">
        {autoState === "saving" && <span className="text-xs text-muted-foreground" data-testid="p1-autosave">Autosaving…</span>}
        {autoState === "saved" && <span className="text-xs text-emerald-600" data-testid="p1-autosave">✓ Draft autosaved</span>}
        <Button data-testid="p1-save-continue" onClick={save} disabled={saving}
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-sm font-semibold">
          {saving ? "Saving…" : "Save & Continue"} <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}
